import React, { useCallback, useMemo, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { HiOutlineClipboardCopy } from "react-icons/hi";
import { classNames } from "Utils/utils";

/** Strip react-markdown / remark internal props before spreading to DOM. */
const getMarkdownDomProps = ({
  node: _node,
  inline: _inline,
  ordered: _ordered,
  checked: _checked,
  index: _index,
  isHeader: _isHeader,
  level: _level,
  depth: _depth,
  ...domProps
}) => domProps;

const CodeBlock = ({ children, isDark }) => {
  const [copied, setCopied] = useState(false);
  const codeText = String(children).replace(/\n$/, "");

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(codeText);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch (_) {
      // Silent fail.
    }
  }, [codeText]);

  return (
    <div className="group/code relative my-3">
      <button
        type="button"
        onClick={handleCopy}
        className="absolute right-2 top-2 z-[1] inline-flex items-center gap-1 rounded-md border border-white/10 bg-white/10 px-2 py-1 text-[10px] font-medium text-slate-300 opacity-0 backdrop-blur-sm transition-all hover:bg-white/20 group-hover/code:opacity-100"
        aria-label="Copy code"
      >
        <HiOutlineClipboardCopy className="h-3 w-3" />
        {copied ? "Copied" : "Copy"}
      </button>
      <pre
        className={classNames(
          "custom-scroll overflow-x-auto rounded-xl px-4 py-3.5 text-[12.5px] leading-relaxed",
          isDark ? "bg-black/25 text-white" : "bg-[#0F172A] text-slate-100 shadow-inner"
        )}
      >
        <code>{children}</code>
      </pre>
    </div>
  );
};

/**
 * Renders Copilot message content as GitHub-flavoured markdown.
 * Streaming-ready: content can be updated incrementally and re-rendered.
 *
 * @param {{ content: string, tone?: 'light' | 'dark' }} props
 */
const MarkdownMessage = ({ content, tone = "light" }) => {
  const isDark = tone === "dark";

  const components = useMemo(
    () => ({
      p: (props) => (
        <p
          className="mb-3 last:mb-0 text-[13.5px] leading-[1.65] text-slate-700"
          {...getMarkdownDomProps(props)}
        />
      ),
      a: ({ children, ...props }) => (
        // eslint-disable-next-line jsx-a11y/anchor-has-content
        <a
          {...getMarkdownDomProps(props)}
          target="_blank"
          rel="noopener noreferrer"
          className={classNames(
            "font-semibold underline decoration-[#4B6CB7]/30 underline-offset-2 transition-colors break-words",
            isDark ? "text-white/90 hover:text-white" : "text-[#4B6CB7] hover:text-[#293F6A]"
          )}
        >
          {children}
        </a>
      ),
      strong: (props) => (
        <strong
          className="font-bold text-slate-900"
          {...getMarkdownDomProps(props)}
        />
      ),
      em: (props) => <em className="italic text-slate-600" {...getMarkdownDomProps(props)} />,
      ul: (props) => (
        <ul
          className="mb-3 last:mb-0 ml-4 list-disc space-y-2"
          {...getMarkdownDomProps(props)}
        />
      ),
      ol: (props) => (
        <ol
          className="mb-3 last:mb-0 ml-4 list-decimal space-y-2"
          {...getMarkdownDomProps(props)}
        />
      ),
      li: ({ children, ...props }) => (
        <li
          className="text-[13.5px] leading-[1.6] text-slate-700"
          {...getMarkdownDomProps({ children, ...props })}
        >
          {children}
        </li>
      ),
      h1: ({ children, ...props }) => (
        <h1
          className="mb-2 mt-3 text-base font-bold text-slate-900"
          {...getMarkdownDomProps(props)}
        >
          {children}
        </h1>
      ),
      h2: ({ children, ...props }) => (
        <h2
          className="mb-2 mt-3 text-[15px] font-bold text-slate-900"
          {...getMarkdownDomProps(props)}
        >
          {children}
        </h2>
      ),
      h3: ({ children, ...props }) => (
        <h3
          className="mb-1.5 mt-2 text-[14px] font-semibold text-slate-800"
          {...getMarkdownDomProps(props)}
        >
          {children}
        </h3>
      ),
      blockquote: (props) => (
        <blockquote
          className={classNames(
            "my-3 rounded-r-lg border-l-[3px] py-1 pl-4 text-[13px] italic",
            isDark
              ? "border-white/40 text-white/80"
              : "border-[#4B6CB7]/40 bg-[#ECF0FD]/40 text-slate-600"
          )}
          {...getMarkdownDomProps(props)}
        />
      ),
      code: ({ inline, children, ...props }) => {
        if (inline) {
          return (
            <code
              className={classNames(
                "rounded-md px-1.5 py-0.5 font-mono text-[0.85em] font-medium",
                isDark ? "bg-white/20 text-white" : "bg-[#ECF0FD] text-[#293F6A]"
              )}
              {...getMarkdownDomProps(props)}
            >
              {children}
            </code>
          );
        }
        return <CodeBlock isDark={isDark}>{children}</CodeBlock>;
      },
      pre: ({ children }) => <>{children}</>,
      table: (props) => (
        <div className="custom-scroll my-3 overflow-x-auto rounded-xl border border-slate-200/80">
          <table
            className={classNames(
              "w-full border-collapse text-[12.5px]",
              isDark ? "text-white" : "text-slate-700"
            )}
            {...getMarkdownDomProps(props)}
          />
        </div>
      ),
      th: (props) => (
        <th
          className={classNames(
            "border-b px-3 py-2 text-left text-xs font-bold uppercase tracking-wide",
            isDark ? "border-white/25 bg-white/10" : "border-slate-200 bg-[#F4F7FE] text-slate-600"
          )}
          {...getMarkdownDomProps(props)}
        />
      ),
      td: (props) => (
        <td
          className={classNames(
            "border-b px-3 py-2.5 align-top",
            isDark ? "border-white/25" : "border-slate-100"
          )}
          {...getMarkdownDomProps(props)}
        />
      ),
      hr: () => (
        <hr
          className={classNames(
            "my-4 border-0 border-t",
            isDark ? "border-white/25" : "border-slate-200"
          )}
        />
      ),
    }),
    [isDark]
  );

  return (
    <div className="copilot-markdown [&>*:last-child]:mb-0">
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
        {String(content || "")}
      </ReactMarkdown>
    </div>
  );
};

export default MarkdownMessage;
